import { CanAccess, useGetList, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type { AlertRule, DealActivityType, DealStageConfig } from "../types";
import {
  resolveAlertRuleDescription,
  resolveAlertRuleThresholdSummary,
} from "./alertRuleForm";

/**
 * Almikia Epic ER (TASK-006, vision #12): "Alertas" -- direccion-only
 * (mirrors `DealStageConfigList`/`DealActivityTypeList`'s `CanAccess` gate:
 * every role reads the RLS-backed catalogue, only direccion may reach this
 * screen at all). Each row shows the rule's label, an on/off `StatusPill`, a
 * plain-Spanish description of when the alert fires and its current
 * threshold. No create/delete action (the 4 seeded rules are the whole
 * catalogue for the lifetime of this epic, TASK-003's ADR).
 */
export const AlertRuleList = () => (
  <CanAccess
    resource="alert_rules"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <AlertRuleListContent />
  </CanAccess>
);

const AlertRuleListContent = () => {
  const translate = useTranslate();

  const {
    data: rules,
    isPending: rulesPending,
    error: rulesError,
    refetch: refetchRules,
  } = useGetList<AlertRule>("alert_rules", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" },
  });
  const {
    data: stageConfigs,
    isPending: stageConfigsPending,
    error: stageConfigsError,
    refetch: refetchStageConfigs,
  } = useGetList<DealStageConfig>("deal_stage_configs", {
    pagination: { page: 1, perPage: 100 },
  });
  const {
    data: activityTypes,
    isPending: activityTypesPending,
    error: activityTypesError,
    refetch: refetchActivityTypes,
  } = useGetList<DealActivityType>("deal_activity_types", {
    pagination: { page: 1, perPage: 200 },
  });

  if (rulesError || stageConfigsError || activityTypesError) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => {
          refetchRules();
          refetchStageConfigs();
          refetchActivityTypes();
        }}
      />
    );
  }
  if (
    rulesPending ||
    stageConfigsPending ||
    activityTypesPending ||
    !rules ||
    !stageConfigs ||
    !activityTypes
  ) {
    return <ListCardsSkeleton rows={4} />;
  }

  const stageLabelByKey = new Map(
    stageConfigs.map((stage) => [stage.key, stage.label]),
  );
  const activityNameById = new Map(
    activityTypes.map((activity) => [activity.id, activity.name]),
  );

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between px-1">
        <h1 className="text-xl font-semibold">
          {translate("resources.alert_rules.title")}
        </h1>
      </div>

      {rules.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            {translate("resources.alert_rules.empty")}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {rules.map((rule) => {
              const description = resolveAlertRuleDescription(
                rule,
                stageLabelByKey,
                activityNameById,
              );
              const thresholdSummary = resolveAlertRuleThresholdSummary(
                rule,
                stageLabelByKey,
                activityNameById,
              );
              return (
                <Link
                  key={rule.id}
                  to={`/alert_rules/${rule.id}`}
                  className="flex items-center gap-3 p-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium truncate">{rule.label}</span>
                      <StatusPill
                        tone={rule.enabled ? "ok" : "not-ok"}
                        text={translate(
                          rule.enabled
                            ? "resources.alert_rules.status.enabled"
                            : "resources.alert_rules.status.disabled",
                        )}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {translate(description.key, description.params)}
                    </p>
                    <p className="text-sm text-muted-foreground tabular-nums">
                      {translate(thresholdSummary.key, thresholdSummary.params)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
